import { useRef, useState } from 'react';
import { App, Button, Progress, Tooltip, Typography } from 'antd';
import { DeleteOutlined, FileOutlined, SwapOutlined, UploadOutlined } from '@ant-design/icons';
import { container } from '@infrastructure/di/container';

export interface FilePickerValue {
  /** The staged upload's id (used server-side to resolve the file when the request is sent). */
  uploadId: string;
  filename: string;
  mimeType: string | null;
  size: number;
}

interface FilePickerProps {
  value: FilePickerValue | null;
  onChange: (value: FilePickerValue | null) => void;
  label?: string;
}

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const MAX_BYTES = 50 * 1024 * 1024;

/**
 * A real browser file picker for a request's binary body / a multipart
 * file field — no `accept` restriction (any file type is a legitimate
 * request payload), genuine upload progress via axios's onUploadProgress
 * (not a simulated percentage), and a hard client-side size pre-check
 * mirroring the backend's 50MB limit so a huge file fails fast instead of
 * uploading most of the way before being rejected.
 */
export const FilePicker = ({ value, onChange, label = 'file' }: FilePickerProps) => {
  const { message } = App.useApp();
  const inputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file: File) => {
    if (file.size > MAX_BYTES) {
      void message.error(`"${file.name}" is ${formatBytes(file.size)} — the limit is 50 MB.`);
      return;
    }

    const previousUploadId = value?.uploadId ?? null;
    const controller = new AbortController();
    abortRef.current = controller;
    setUploading(true);
    setProgress(0);

    try {
      const staged = await container.uploads.upload(file, setProgress, controller.signal);
      onChange({
        uploadId: staged.id,
        filename: staged.filename,
        mimeType: staged.mimeType,
        size: staged.size,
      });
      if (previousUploadId) {
        container.uploads.remove(previousUploadId).catch(() => {});
      }
    } catch (error) {
      if (controller.signal.aborted) {
        void message.info('Upload cancelled');
      } else {
        void message.error(error instanceof Error ? error.message : 'Upload failed');
      }
    } finally {
      setUploading(false);
      setProgress(null);
      abortRef.current = null;
    }
  };

  const handleRemove = () => {
    if (uploading) {
      abortRef.current?.abort();
      return;
    }
    if (value) {
      container.uploads.remove(value.uploadId).catch(() => {});
    }
    onChange(null);
  };

  return (
    <div className="flex items-center gap-2">
      <input
        ref={inputRef}
        type="file"
        hidden
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (file) void handleFile(file);
        }}
      />

      {!value && !uploading && (
        <Button size="small" icon={<UploadOutlined />} onClick={() => inputRef.current?.click()}>
          Choose file…
        </Button>
      )}

      {uploading && (
        <div className="flex min-w-0 flex-1 items-center gap-2">
          <Progress percent={progress ?? 0} size="small" className="max-w-xs flex-1" />
          <Button size="small" danger onClick={handleRemove}>
            Cancel
          </Button>
        </div>
      )}

      {value && !uploading && (
        <div className="flex min-w-0 items-center gap-2 rounded border border-black/10 px-2 py-1 text-xs dark:border-white/10">
          <FileOutlined className="shrink-0 text-gray-400" />
          <Tooltip title={value.filename}>
            <Typography.Text className="max-w-[180px] truncate text-xs">
              {value.filename}
            </Typography.Text>
          </Tooltip>
          <span className="shrink-0 text-gray-400">{formatBytes(value.size)}</span>
          {value.mimeType && <span className="shrink-0 text-gray-400">{value.mimeType}</span>}
          <Tooltip title="Replace file">
            <Button
              type="text"
              size="small"
              icon={<SwapOutlined />}
              aria-label={`Replace ${label}`}
              onClick={() => inputRef.current?.click()}
            />
          </Tooltip>
          <Tooltip title="Remove file">
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              aria-label={`Remove ${label}`}
              onClick={handleRemove}
            />
          </Tooltip>
        </div>
      )}
    </div>
  );
};
